import { useMutation, useQuery } from '@apollo/client/react'
import { Button, FieldMessage, LoadingState, useLanguage, useTranslate } from '@athena/ui'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { AuthScreen } from '../features/auth/AuthScreen.js'
import { SignInPanel } from '../features/auth/SignInPanel.js'
import { AcceptInvitationDocument, MeDocument } from '../graphql/generated/operations.js'
import { useFirstUserErrorText } from '../lib/user-errors.js'
import { OnboardingScreen } from './OnboardingScreen.js'

/** Compatibility for previously issued links only. Acceptance requires an explicit click. */
export function InviteScreen() {
  const t = useTranslate()
  const language = useLanguage()
  const userErrorText = useFirstUserErrorText()
  const navigate = useNavigate()
  const { data, loading, refetch } = useQuery(MeDocument)
  const [accept, { loading: accepting }] = useMutation(AcceptInvitationDocument)
  const [error, setError] = useState<string>()
  const viewer = data?.me
  async function acceptPending() {
    setError(undefined)
    try {
      const result = await accept()
      const payload = result.data?.acceptInvitation
      if (!payload?.student) {
        setError(userErrorText(payload?.userErrors, t('auth.inviteFailed')))
        return
      }
      await refetch()
      await navigate({
        to: viewer?.student ? '/$language/learn' : '/$language',
        params: { language },
        replace: true,
      })
    } catch {
      setError(t('auth.inviteFailed'))
    }
  }
  if (loading && !viewer) return <LoadingState>{t('common.loading')}</LoadingState>
  if (viewer && !viewer.onboarding.complete) return <OnboardingScreen invitation />
  return (
    <AuthScreen title={t('auth.inviteTitle')} subtitle={t('auth.inviteSubtitle')}>
      {viewer ? (
        <div className="flex flex-col items-start gap-4">
          {error ? <FieldMessage tone="error">{error}</FieldMessage> : null}
          <Button disabled={accepting} loading={accepting} onClick={() => void acceptPending()}>
            {t(error ? 'auth.inviteRetry' : 'connections.legacyAccept')}
          </Button>
        </div>
      ) : (
        <SignInPanel
          callbackPath="/invite"
          submitLabel={t('auth.submitInvite')}
          onSignedIn={() => {
            void refetch()
          }}
        />
      )}
    </AuthScreen>
  )
}
